import { z } from "zod";

/**
 * JSON body → JSON response operations
 * ParseWord, ExtractVariablesFromHtml, GenerateFromTemplate, GenerateWordFromInstructions
 */

// ============================================================================
// PARSE WORD
// ============================================================================

/**
 * `fileUrl` is FETCHED SERVER-SIDE, so it is an SSRF sink and this schema is the
 * first of two layers that close it.
 *
 * It was `z.string()` — not even `z.url()` — so `file:///etc/passwd` and
 * `http://169.254.169.254/latest/meta-data/` were both accepted and both
 * dialled from inside the cluster, with the parsed result returned to the
 * caller. Requiring a well-formed absolute HTTPS URL here turns the malformed
 * and non-HTTPS cases into a 400 at the contract, which is a route that visibly
 * refuses rather than one that silently never runs.
 *
 * ⚠️ **This is well-formedness, NOT the SSRF control.** `z.url()` accepts any
 * host, so an https URL naming an internal service still parses. The control is
 * the ORIGIN ALLOW-LIST at the fetch itself —
 * `apps/backend/src/_common/documents/fetch-template-document.ts` — which is
 * also where redirects are refused. Both layers are load-bearing: a
 * `DocumentTemplate.fileUrl` row persisted before this schema existed reaches
 * the same sink without passing through here at all.
 */
export const DocumentTemplateParseWordBodySchema = z.object({
  fileUrl: z.string().url().startsWith("https://", "fileUrl must be an https URL")
});

export const DocumentTemplateParseWordResponseSchema = z.object({
  tags: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]),
  error: z.unknown().optional(),
  imagesUrls: z.array(z.string())
});

// ============================================================================
// EXTRACT VARIABLES FROM HTML
// ============================================================================

export const DocumentTemplateExtractVariablesFromHtmlBodySchema = z.object({
  html: z.string().min(1)
});

// ============================================================================
// GENERATE FROM TEMPLATE
// ============================================================================

export const DocumentTemplateGenerateFromTemplateBodySchema = z.object({
  templateId: z.string(),
  variables: z.record(z.string(), z.unknown())
});

export const DocumentTemplateGenerateFromTemplateResponseSchema = z.object({
  url: z.string()
});

// ============================================================================
// GENERATE WORD FROM INSTRUCTIONS
// ============================================================================

export const DocumentTemplateGenerateWordFromInstructionsBodySchema = z.object({
  fileUrl: z.string(),
  instructions: z.string(),
  tags: z.record(z.string(), z.unknown()).optional(),
  formatTemplateId: z.string(),
  htmlContent: z.string().optional()
});
